/// <amd-module name="Dashboards/DashboardTemplates/TemplateData" />

import * as Resources from "Dashboards/DashboardTemplates/Resources";

import { Widget } from "Dashboards/Common/Contracts/Dashboard";

export interface TemplateWidgetDescription {
    contributionId: string;
    name: string;
    description: string;
}

export interface TemplatePreviewImageEntry {
    /**
     * An identifier for the template (like "TeamOverview" for the Team Overview template)
     */
    type: string;
    /**
     * Array containing preview image data for template widgets
     */
    images: TemplatePreviewImageData[];
}

export interface TemplatePreviewImageData {
    /**
     * Contribution ID of the widget associated with the image
     */
    contributionId: string;
    /**
     * Light theme widget preview image, formatted as the file path starting with vss-dashboards and with the prefix "ms."
     */
    previewLight: string;
    /**
     * Dark theme widget preview image, formatted as the file path starting with vss-dashboards and with the prefix "ms."
     */
    previewDark: string;
}

export interface TemplatePreviewText {
    /**
     * An identifier for the template (like "TeamOverview" for the Team Overview template)
     */
    type: string;
    /**
     * CSS style id associated with template preview
     */
    styleId: string;
    /**
     * Customer-facing name of the template
     */
    title: string;
    /**
     * Customer-facing description of the template to display in the preview dialog
     */
    description: string;
}

export interface TemplateCardData {
    /**
     * An identifier for the template (like "TeamOverview" for the Team Overview template)
     */
    type: string;
    /**
     * Customer-facing name of the template
     */
    title: string;
    /**
     * Customer-facing tagline of the template
     */
    subtitle: string;
    /**
     * Light theme thumbnail of the template, formatted as the file path starting with vss-dashboards and with the prefix "ms."
     */
    thumbnailLight: string;
    /**
     * Dark theme thumbnail of the template, formatted as the file path starting with vss-dashboards and with the prefix "ms."
     */
    thumbnailDark: string;
}

export interface TemplateDataEntry {
    /**
     * An identifier for the template (like "TeamOverview" for the Team Overview template)
     */
    type: string;
    /**
     * Array of information from which to create template widgets
     */
    widgets: TemplateWidgetData[];
}

export interface TemplateWidgetData extends Pick<Widget, "contributionId" | "size" | "position" | "settings" | "id"> {}

export function getTemplatePreviewImages(): TemplatePreviewImageEntry[] {
    return TemplatePreviewImages;
}

export function getTemplatePreviewText(): TemplatePreviewText[] {
    return TemplatePreviewText;
}

export function getTemplateCardProps(): TemplateCardData[] {
    return TemplateCardData;
}

export function getTemplateData(): TemplateDataEntry[] {
    return TemplateData;
}

/** Contains the widget preview images for each template */
export const TemplatePreviewImages: TemplatePreviewImageEntry[] = [
    {
        type: "TeamOverview",
        images: [
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.CycleTimeWidget",
                previewLight: "ms.vss-dashboards/dashboards-templates/images/widgets/cycle-time/3x2CycleTimeLight.svg",
                previewDark: "ms.vss-dashboards/dashboards-templates/images/widgets/cycle-time/3x2CycleTimeDark.svg"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.LeadTimeWidget",
                previewLight: "ms.vss-dashboards/dashboards-templates/images/widgets/lead-time/3x2LeadTimeLight.svg",
                previewDark: "ms.vss-dashboards/dashboards-templates/images/widgets/lead-time/3x2LeadTimeDark.svg"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.VelocityWidget",
                previewLight: "ms.vss-dashboards/dashboards-templates/images/widgets/velocity/3x2VelocityLight.svg",
                previewDark: "ms.vss-dashboards/dashboards-templates/images/widgets/velocity/3x2VelocityDark.svg"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.TeamMembersWidget",
                previewLight: "ms.vss-dashboards/dashboards-templates/images/widgets/team-members/2x1TeamMembersLight.svg",
                previewDark: "ms.vss-dashboards/dashboards-templates/images/widgets/team-members/2x1TeamMembersDark.svg"
            },
            {
                contributionId: "ms.vss-mywork-web.Microsoft.VisualStudioOnline.MyWork.PullRequestWidget",
                previewLight: "ms.vss-dashboards/dashboards-templates/images/widgets/pull-requests/3x2PullRequestsLight.svg",
                previewDark: "ms.vss-dashboards/dashboards-templates/images/widgets/pull-requests/3x2PullRequestsDark.svg"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.PullrequestsWidget",
                previewLight: "ms.vss-dashboards/dashboards-templates/images/widgets/pull-requests/3x2PullRequestsLight.svg",
                previewDark: "ms.vss-dashboards/dashboards-templates/images/widgets/pull-requests/3x2PullRequestsDark.svg"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.NewWorkItemWidget",
                previewLight: "ms.vss-dashboards/dashboards-templates/images/widgets/new-work-item/2x1NewWorkItemLight.svg",
                previewDark: "ms.vss-dashboards/dashboards-templates/images/widgets/new-work-item/2x1NewWorkItemDark.svg"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.AssignedToMeWidget",
                previewLight: "ms.vss-dashboards/dashboards-templates/images/widgets/assigned-to-me/2x2AssignedToMeLight.svg",
                previewDark: "ms.vss-dashboards/dashboards-templates/images/widgets/assigned-to-me/2x2AssignedToMeDark.svg"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.BuildChartWidget",
                previewLight: "ms.vss-dashboards/dashboards-templates/images/widgets/build-history/2x1BuildHistoryLight.svg",
                previewDark: "ms.vss-dashboards/dashboards-templates/images/widgets/build-history/2x1BuildHistoryDark.svg"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.CodeScalarWidget",
                previewLight: "ms.vss-dashboards/dashboards-templates/images/widgets/code-tile/1x1CodeTileLight.svg",
                previewDark: "ms.vss-dashboards/dashboards-templates/images/widgets/code-tile/1x1CodeTileDark.svg"
            }
        ]
    },
    {
        type: "SprintManagement",
        images: [
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.VelocityWidget",
                previewLight: "ms.vss-dashboards/dashboards-templates/images/widgets/velocity/3x2VelocityLight.svg",
                previewDark: "ms.vss-dashboards/dashboards-templates/images/widgets/velocity/3x2VelocityDark.svg"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.SprintCapacityWidget",
                previewLight: "ms.vss-dashboards/dashboards-templates/images/widgets/sprint-capacity/2x1SprintCapacityLight.svg",
                previewDark: "ms.vss-dashboards/dashboards-templates/images/widgets/sprint-capacity/2x1SprintCapacityDark.svg"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.SprintOverviewWidget",
                previewLight: "ms.vss-dashboards/dashboards-templates/images/widgets/sprint-overview/2x1SprintOverviewLight.svg",
                previewDark: "ms.vss-dashboards/dashboards-templates/images/widgets/sprint-overview/2x1SprintOverviewDark.svg"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.SprintBurndownWidget",
                previewLight: "ms.vss-dashboards/dashboards-templates/images/widgets/sprint-burndown/2x1SprintBurndownLight.svg",
                previewDark: "ms.vss-dashboards/dashboards-templates/images/widgets/sprint-burndown/2x1SprintBurndownDark.svg"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.CodeScalarWidget",
                previewLight: "ms.vss-dashboards/dashboards-templates/images/widgets/code-tile/1x1CodeTileLight.svg",
                previewDark: "ms.vss-dashboards/dashboards-templates/images/widgets/code-tile/1x1CodeTileDark.svg"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.CumulativeFlowDiagramWidget",
                previewLight: "ms.vss-dashboards/dashboards-templates/images/widgets/cfd/3x2CFDLight.svg",
                previewDark: "ms.vss-dashboards/dashboards-templates/images/widgets/cfd/3x2CFDDark.svg"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.BurndownWidget",
                previewLight: "ms.vss-dashboards/dashboards-templates/images/widgets/burndown/4x3BurndownLight.svg",
                previewDark: "ms.vss-dashboards/dashboards-templates/images/widgets/burndown/4x3BurndownDark.svg"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.AssignedToMeWidget",
                previewLight: "ms.vss-dashboards/dashboards-templates/images/widgets/assigned-to-me/2x2AssignedToMeLight.svg",
                previewDark: "ms.vss-dashboards/dashboards-templates/images/widgets/assigned-to-me/2x2AssignedToMeDark.svg"
            }
        ]
    }
];

/** Contains the text and styling class information for each template preview */
export const TemplatePreviewText: TemplatePreviewText[] = [
    {
        type: "TeamOverview",
        styleId: "team-overview",
        title: Resources.DashboardTemplatePage_TeamOverview_Title,
        description: Resources.DashboardTemplatePage_TeamOverview_Description
    },
    {
        type: "SprintManagement",
        styleId: "sprint-management",
        title: Resources.DashboardTemplatePage_SprintManagement_Title,
        description: Resources.DashboardTemplatePage_SprintManagement_Description
    }
];

/** Contains text/images for template page cards */
export const TemplateCardData: TemplateCardData[] = [
    {
        type: "Blank",
        title: Resources.DashboardTemplatePage_Blank_Title,
        subtitle: Resources.DashboardTemplatePage_Blank_Subtitle,
        thumbnailLight: "ms.vss-dashboards/dashboards-templates/images/thumbnails/BlankThumbnailLight.svg",
        thumbnailDark: "ms.vss-dashboards/dashboards-templates/images/thumbnails/BlankThumbnailDark.svg"
    },
    {
        type: "TeamOverview",
        title: Resources.DashboardTemplatePage_TeamOverview_Title,
        subtitle: Resources.DashboardTemplatePage_TeamOverview_Subtitle,
        thumbnailLight: "ms.vss-dashboards/dashboards-templates/images/thumbnails/TeamOverviewThumbnailLight.svg",
        thumbnailDark: "ms.vss-dashboards/dashboards-templates/images/thumbnails/TeamOverviewThumbnailDark.svg"
    },
    {
        type: "SprintManagement",
        title: Resources.DashboardTemplatePage_SprintManagement_Title,
        subtitle: Resources.DashboardTemplatePage_SprintManagement_Subtitle,
        thumbnailLight: "ms.vss-dashboards/dashboards-templates/images/thumbnails/SprintManagementThumbnailLight.svg",
        thumbnailDark: "ms.vss-dashboards/dashboards-templates/images/thumbnails/SprintManagementThumbnailDark.svg"
    }
];

/** Data for dashboard client for template widgets, including widget type, size, positioning, settings, and unique id. */
export const TemplateData: TemplateDataEntry[] = [
    {
        type: "TeamOverview",
        widgets: [
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.CycleTimeWidget",
                size: {
                    columnSpan: 3,
                    rowSpan: 2
                },
                position: {
                    column: 1,
                    row: 1
                },
                settings: "",
                id: "0"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.LeadTimeWidget",
                size: {
                    columnSpan: 3,
                    rowSpan: 2
                },
                position: {
                    column: 1,
                    row: 3
                },
                settings: "",
                id: "1"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.VelocityWidget",
                size: {
                    columnSpan: 3,
                    rowSpan: 2
                },
                position: {
                    column: 1,
                    row: 5
                },
                settings: "",
                id: "2"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.TeamMembersWidget",
                size: {
                    columnSpan: 2,
                    rowSpan: 1
                },
                position: {
                    column: 4,
                    row: 1
                },
                settings: "",
                id: "3"
            },
            {
                contributionId: "ms.vss-mywork-web.Microsoft.VisualStudioOnline.MyWork.PullRequestWidget",
                size: {
                    columnSpan: 3,
                    rowSpan: 2
                },
                position: {
                    column: 4,
                    row: 3
                },
                settings: "",
                id: "4"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.PullrequestsWidget",
                size: {
                    columnSpan: 3,
                    rowSpan: 2
                },
                position: {
                    column: 1,
                    row: 7
                },
                settings: "",
                id: "5"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.NewWorkItemWidget",
                size: {
                    columnSpan: 2,
                    rowSpan: 1
                },
                position: {
                    column: 4,
                    row: 5
                },
                settings: "",
                id: "6"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.AssignedToMeWidget",
                size: {
                    columnSpan: 2,
                    rowSpan: 2
                },
                position: {
                    column: 4,
                    row: 6
                },
                settings: "",
                id: "7"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.BuildChartWidget",
                size: {
                    columnSpan: 2,
                    rowSpan: 1
                },
                position: {
                    column: 6,
                    row: 1
                },
                settings: "",
                id: "8"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.CodeScalarWidget",
                size: {
                    columnSpan: 1,
                    rowSpan: 1
                },
                position: {
                    column: 6,
                    row: 5
                },
                settings: "",
                id: "9"
            }
        ]
    },
    {
        type: "SprintManagement",
        widgets: [
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.VelocityWidget",
                size: {
                    columnSpan: 3,
                    rowSpan: 2
                },
                position: {
                    column: 1,
                    row: 1
                },
                settings: "",
                id: "0"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.SprintCapacityWidget",
                size: {
                    columnSpan: 2,
                    rowSpan: 1
                },
                position: {
                    column: 1,
                    row: 3
                },
                settings: "",
                id: "1"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.SprintOverviewWidget",
                size: {
                    columnSpan: 2,
                    rowSpan: 1
                },
                position: {
                    column: 1,
                    row: 4
                },
                settings: "",
                id: "2"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.SprintBurndownWidget",
                size: {
                    columnSpan: 2,
                    rowSpan: 1
                },
                position: {
                    column: 1,
                    row: 5
                },
                settings: "",
                id: "3"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.CodeScalarWidget",
                size: {
                    columnSpan: 1,
                    rowSpan: 1
                },
                position: {
                    column: 3,
                    row: 3
                },
                settings: "",
                id: "4"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.CumulativeFlowDiagramWidget",
                size: {
                    columnSpan: 3,
                    rowSpan: 2
                },
                position: {
                    column: 3,
                    row: 4
                },
                settings: "",
                id: "5"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.BurndownWidget",
                size: {
                    columnSpan: 4,
                    rowSpan: 3
                },
                position: {
                    column: 4,
                    row: 1
                },
                settings: "",
                id: "6"
            },
            {
                contributionId: "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.AssignedToMeWidget",
                size: {
                    columnSpan: 2,
                    rowSpan: 2
                },
                position: {
                    column: 6,
                    row: 4
                },
                settings: "",
                id: "7"
            }
        ]
    }
];
